import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { BackupLenderTemplate } from '~/backup/lender/domains/page/ui/BackupLenderTemplate';
import { AnalyticsView } from '~/backup/lender/views/analytics/AnalyticsView';
import { PortfolioView } from '~/backup/lender/views/portfolio/PortfolioView';
import { AgentView } from '~/backup/shared/views/agent/AgentView';
import { BackupDataPipelineRoutes } from '~/backup/shared/views/data-pipeline/DataPipelineRoutes';
import { CycleRedirect } from '~/backup/shared/views/deal-overview/CycleRedirect';
import { DealOverviewView } from '~/backup/shared/views/deal-overview/DealOverviewView';
import { DealPageRedirect } from '~/backup/shared/views/deal-overview/DealPageRedirect';
import { DealSettingsHub } from '~/backup/shared/views/deal-settings/DealSettingsHub';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { DefaultErrorRoute } from '~/core/DefaultErrorRoute';

export const BackupLenderRouter = (): React.ReactElement => {
  const flags = useFeatureFlags();
  return (
    <BackupLenderTemplate>
      <Routes>
        <Route
          path="/portfolio"
          element={
            <RouteErrorBoundary
              resetAfterCatch={false}
              fallback={({ message, showErrorToast }) => {
                showErrorToast(message);
                return <DefaultErrorRoute />;
              }}
            >
              <PortfolioView />
            </RouteErrorBoundary>
          }
        />
        {/* Always routed; the nav shows it only for orgs with Omni analytics
            configured, and the embed itself says so for any other org. */}
        <Route
          path="/analytics"
          element={
            <RouteErrorBoundary
              resetAfterCatch={false}
              fallback={({ message, showErrorToast }) => {
                showErrorToast(message);
                return <DefaultErrorRoute />;
              }}
            >
              <AnalyticsView />
            </RouteErrorBoundary>
          }
        />
        {/* The page was called Strats; keep old links working. */}
        <Route path="/strats" element={<Navigate to="/analytics" replace />} />
        <Route
          path="/deals/:implementationId"
          element={
            <RouteErrorBoundary
              resetAfterCatch={false}
              fallback={({ message, showErrorToast }) => {
                showErrorToast(message);
                return <DefaultErrorRoute />;
              }}
            >
              <DealOverviewView />
            </RouteErrorBoundary>
          }
        />
        {/* A period renders the same Overview, scoped to that period. */}
        <Route
          path="/deals/:implementationId/periods/:reportingPeriod"
          element={
            <RouteErrorBoundary
              resetAfterCatch={false}
              fallback={({ message, showErrorToast }) => {
                showErrorToast(message);
                return <DefaultErrorRoute />;
              }}
            >
              <DealOverviewView />
            </RouteErrorBoundary>
          }
        />
        {/* Legacy paths, kept so existing links / bookmarks still land. The period
            list and files now live on the deal page itself. */}
        <Route
          path="/deals/:implementationId/cycles/:cycleId"
          element={<CycleRedirect />}
        />
        <Route
          path="/deals/:implementationId/deliverables"
          element={<DealPageRedirect />}
        />
        <Route
          path="/deals/:implementationId/files"
          element={<DealPageRedirect />}
        />
        <Route
          path="/deals/:implementationId/settings/*"
          element={
            <RouteErrorBoundary
              resetAfterCatch={false}
              fallback={({ message, showErrorToast }) => {
                showErrorToast(message);
                return <DefaultErrorRoute />;
              }}
            >
              <DealSettingsHub />
            </RouteErrorBoundary>
          }
        />
        <Route path="/data-pipeline/*" element={<BackupDataPipelineRoutes />} />
        {flags.productsBackupAssistantPermissions === 'allow' && (
          <Route
            path="/agent"
            element={
              <RouteErrorBoundary
                resetAfterCatch={false}
                fallback={({ message, showErrorToast }) => {
                  showErrorToast(message);
                  return <DefaultErrorRoute />;
                }}
              >
                <AgentView />
              </RouteErrorBoundary>
            }
          />
        )}
        <Route path="/" element={<Navigate to="/portfolio" replace />} />
        <Route path="/*" element={<Navigate to="/portfolio" replace />} />
      </Routes>
    </BackupLenderTemplate>
  );
};
